/**
 * Prefetch + JS-кеш для GET /api/v1/fields/map-init.
 *
 * Mirror of frontend/src/lib/mapInitCache.ts (production). Port для frontend-vite.
 *
 * Используется через getMapInit() в MapCanvas — возвращает массивы маркеров
 * (companies / clusters / farms / sub_markers) для агрегированного отображения
 * полей при выходе за zoom level (chips приhide когда poll'я видны индивидуально).
 *
 * ## Кеш-стратегия (2026-06-05)
 *
 * - **TTL=60s**: совпадает с backend Cache-Control max-age=60. После истечения
 *   getMapInit() делает revalidate-запрос — но НЕ ждёт ответа, отдаёт stale
 *   данные сразу (UX без лагов). На фоне fetch'ит свежие. Когда придут —
 *   обновляет cache; компонент-потребитель re-render'нится при следующем
 *   getMapInit() в ближайшем зум-чейндже / scope-changed.
 *
 * - **ETag revalidate**: backend генерирует ETag = MD5(tenant + max(updated_at)).
 *   Браузер автоматически добавляет If-None-Match в next запрос (благодаря
 *   Cache-Control max-age expired). Backend возвращает **304 Not Modified**
 *   если данные не изменились → fetch очень быстрый (~200 байт),
 *   используем уже кешированный response. Иначе **200 + новые данные**.
 *
 * - **Background revalidate**: после получения свежих данных рассылается
 *   событие `'map-init-updated'` — компоненты могут на него подписаться
 *   и обновить чипы без явного reload.
 *
 * AppShell может запустить prefetchMapInit() заранее (после login) чтобы
 * /map открывался мгновенно.
 */
import { api } from '@/lib/api';

export interface MapMarker {
  id: string;
  name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  lng: number;
  lat: number;
  field_count: number;
  area_ha: number;
  bbox?: [number, number, number, number] | null;
}

export interface ClusterMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  field_count: number;
  farm_count: number;
  area_ha: number;
  bbox?: [number, number, number, number] | null;
}

export interface SubMarker {
  farm_id: string;
  sub_id: number;
  name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  lng: number;
  lat: number;
  field_count: number;
  area_ha: number;
  bbox: [number, number, number, number];
}

export interface MapInitData {
  companies: { id: string; name: string }[];
  clusters: { id: string; company_id: string; name: string }[];
  farms: { id: string; company_id: string; cluster_id: string | null; name: string }[];
  stats: { farm_id: string; brigade_id: string | null; count: number; area_ha: number }[];
  markers: MapMarker[];
  cluster_markers: ClusterMarker[];
  sub_markers: SubMarker[];
}

// 60 seconds — совпадает с backend Cache-Control max-age=60.
// Был 5*60_000 (5 min) что приводило к stale chip-агрегатам после миграций /
// массовых правок (агроном видел старую площадь). Снизили: при каждом zoom-event
// после 60s TTL — revalidate (но stale-while-revalidate отдаёт мгновенно).
const TTL_MS = 60_000;

let cache: Promise<MapInitData> | null = null;
let cachedData: MapInitData | null = null;
let cachedAt = 0;
let revalidateInflight = false;

const UPDATE_EVENT = 'map-init-updated';

function fetchFresh(): Promise<MapInitData> {
  cachedAt = Date.now();
  cache = api
    .get<MapInitData>('/api/v1/fields/map-init')
    .then((data) => {
      cachedData = data;
      // Notify subscribers (MapCanvas, chips, etc.) что данные обновились.
      try {
        window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: data }));
      } catch {
        /* not in browser context */
      }
      return data;
    })
    .catch((e) => {
      cache = null;
      throw e;
    });
  return cache;
}

function revalidateInBackground(): void {
  if (revalidateInflight) return;
  revalidateInflight = true;
  // Браузер автоматически добавит If-None-Match (max-age=60 истёк) →
  // backend вернёт 304 если ничего не изменилось → быстрый revalidate.
  api
    .get<MapInitData>('/api/v1/fields/map-init')
    .then((data) => {
      cachedData = data;
      cachedAt = Date.now();
      cache = Promise.resolve(data);
      try {
        window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: data }));
      } catch {
        /* not in browser context */
      }
    })
    .catch(() => {
      /* keep stale, next getMapInit() retry */
    })
    .finally(() => {
      revalidateInflight = false;
    });
}

export function prefetchMapInit(): void {
  if (cache && Date.now() - cachedAt < TTL_MS) return;
  void fetchFresh();
}

export function getMapInit(): Promise<MapInitData> {
  // No cache yet → block on fetch
  if (!cache) {
    return fetchFresh();
  }
  // TTL expired → return stale immediately, revalidate in background
  // (stale-while-revalidate pattern, matches backend Cache-Control)
  if (Date.now() - cachedAt >= TTL_MS) {
    if (cachedData) {
      revalidateInBackground();
      return Promise.resolve(cachedData);
    }
    // No stale data but cache promise pending → just await it
    return cache;
  }
  return cache;
}

/**
 * Subscribe to background-revalidate events. Returns unsubscribe fn.
 *
 * Используется компонентом-потребителем (MapCanvas) чтобы перерендериться
 * когда фоновый revalidate приносит новые данные.
 */
export function onMapInitUpdate(handler: (data: MapInitData) => void): () => void {
  const listener = (e: Event) => {
    const ce = e as CustomEvent<MapInitData>;
    handler(ce.detail);
  };
  window.addEventListener(UPDATE_EVENT, listener);
  return () => window.removeEventListener(UPDATE_EVENT, listener);
}

export function invalidateMapInit(): void {
  cache = null;
  cachedData = null;
  cachedAt = 0;
}
